import React, { useState, useEffect } from "react";
import { DatePicker } from "antd";
const { RangePicker } = DatePicker;
import { Box, Button, Typography } from "@mui/material";
import moment from "moment";
import AxiosInstance from "../../../services/networkServices";
import { normalizeCrossYearRange } from "./normalizeRange";
import "./datePicker.css";

const dateFormat = "YYYY-MM-DD hh:mm A";

const timePresets = [
  { label: "Start", hour: 0, minute: 0 },
  { label: "Noon", hour: 12, minute: 0 },
  { label: "End", hour: 23, minute: 59 },
];

const DateTimeRangePicker = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  challengeCategory,
  disabledDateCustom,
  disabled = false,
}) => {
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [initialRender, setInitialRender] = useState(true);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(0);
  useEffect(() => {
    if (initialRender) {
      if (startDate) setStart(startDate);
      if (endDate) setEnd(endDate);
      setInitialRender(false);
    }
  }, []);
  useEffect(() => {
    challengeCategory && fetchUnavailableDates(challengeCategory);
  }, [challengeCategory]);

  const handleCalendarChange = (dates) => {
    if (dates) {
      // order is off below so a December → January selection is not sorted
      // into a same-year range that covers every theme in between.
      const [nextStart, nextEnd] = normalizeCrossYearRange(dates[0], dates[1]);
      onStartDateChange(nextStart);
      onEndDateChange(nextEnd);
    } else {
      // Clear icon pressed — dates are optional (TBD)
      onStartDateChange(null);
      onEndDateChange(null);
    }
  };

  const applyTimePreset = (target, hour, minute) => {
    if (target === "start") {
      const base = startDate ? startDate.clone() : moment();
      const updated = base.hour(hour).minute(minute).second(0);
      onStartDateChange(updated);
    } else {
      const base = endDate ? endDate.clone() : moment();
      const updated = base.hour(hour).minute(minute).second(0);
      onEndDateChange(updated);
    }
  };

  const fetchUnavailableDates = async (categoryId) => {
    try {
      const response = await AxiosInstance.get(
        `/category/challenge/unavailable-dates/${categoryId}`
      );
      if (response.data.status) {
        setUnavailableDates(response.data.data.dates);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const disabledDate = (current) => {
    if (
      start &&
      end &&
      current &&
      current >= moment(start).startOf("day") &&
      current <= moment(end).startOf("day") &&
      current >= moment().startOf("day")
    ) {
      return false;
    }
    return (
      (current && current < moment().startOf("day")) ||
      unavailableDates.some(({ startDate, endDate }) => {
        const unavailableStart = moment(startDate);
        const unavailableEnd = moment(endDate);
        return (
          current &&
          current >= moment(unavailableStart).startOf("day") &&
          current <= moment(unavailableEnd).startOf("day")
        );
      })
    );
  };

  const disabledTime = (current) => {
    const now = moment();
    if (current && current.isSame(now, "day")) {
      return {
        disabledHours: () => {
          const hours = [];
          for (let i = 0; i < now.hour(); i++) {
            hours.push(i);
          }
          return hours;
        },
        disabledMinutes: (selectedHour) => {
          if (selectedHour === now.hour()) {
            const minutes = [];
            for (let i = 0; i < now.minute(); i++) {
              minutes.push(i);
            }
            return minutes;
          }
          return [];
        },
        disabledSeconds: () => [],
      };
    }
    return {
      disabledHours: () => [],
      disabledMinutes: () => [],
      disabledSeconds: () => [],
    };
  };

  const renderExtraFooter = () => (
    <Box sx={{ display: "flex", gap: 2, py: 1, px: 0.5, flexWrap: "wrap" }}>
      {["start", "end"].map((target) => (
        <Box key={target} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5, textTransform: "uppercase", color: "#555" }}>
            {target === "start" ? "Start:" : "End:"}
          </Typography>
          {timePresets.map((preset) => (
            <Button
              key={preset.label}
              size="small"
              variant="outlined"
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                e.stopPropagation();
                applyTimePreset(target, preset.hour, preset.minute);
              }}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: "11px",
                lineHeight: 1.4,
                textTransform: "none",
                borderColor: "#d9d9d9",
                color: "#333",
                "&:hover": { borderColor: "#1890ff", color: "#1890ff", background: "#e6f7ff" },
              }}
            >
              {preset.label}
            </Button>
          ))}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ marginTop: "16px", position: "relative", width: "100%" }}>
      <RangePicker
        className="custom-date-time-picker"
        showTime={{ format: "hh:mm A" }}
        format={dateFormat}
        disabled={disabled}
        value={[startDate, endDate]}
        order={false}
        onCalendarChange={handleCalendarChange}
        // disabledDate={disabledDateCustom || disabledDate}
        disabledTime={disabledTime}
        dropdownStyle={{ minWidth: "100%", marginTop: "10px" }}
        style={{ width: "100%" }}
        popupClassName="custom-popup-class"
        renderExtraFooter={renderExtraFooter}
      />
    </Box>
  );
};

export default DateTimeRangePicker;
